import { MarkingPublishingDurationOptionType } from '@/features/analytics/invite-links/const/marking'
import { useInviteLinksStore } from '@/features/analytics/model'
import { useUpdateMarkingFormData } from '@/shared/api'
import { SelectDropdownOptionType } from '@/shared/ui'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

type FormValues = {
    publishedAt: Date | undefined
    deletedDate: Date | undefined
    duration: string | undefined
    paymentMethod: string | undefined
    publishOptionFlag: boolean
}

function toUtcDate(date: Date) {
    return new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    )
}

function computeDeletedDate(
    durationValue: string,
    baseDate: Date | undefined
): Date | undefined {
    if (durationValue === 'no_end_date' || durationValue === 'no_duration') {
        return undefined
    }
    if (!baseDate) return undefined

    const parts = durationValue.split('/')
    if (parts.length !== 2) return undefined
    const hours = parseInt(parts[1], 10)
    if (isNaN(hours)) return undefined

    return new Date(new Date(baseDate).getTime() + hours * 60 * 60 * 1000)
}

export function usePublishOptions(
    markingFormData: ORDMarkingFormData,
    wwld: WorkspaceWithLinkDescriptor
) {
    const updateMarkingFormData = useUpdateMarkingFormData(wwld)
    const [wasCheckboxClicked, setWasCheckboxClicked] = useState(false)
    const { setPublishOptionError, publishOptionError } = useInviteLinksStore()

    const { control, watch, setValue } = useForm<FormValues>({
        defaultValues: {
            publishedAt: markingFormData.publish_date || undefined,
            deletedDate: markingFormData.deleted_date || undefined,
            duration: markingFormData.duration || undefined,
            paymentMethod: markingFormData.payment_method,
            publishOptionFlag: markingFormData.publish_option_flag || false,
        },
    })

    const publishOptionFlag = watch('publishOptionFlag')
    const publishedAt = watch('publishedAt')
    const deletedDate = watch('deletedDate')
    const duration = watch('duration')
    const paymentMethod = watch('paymentMethod')

    const isBlockedByContract = !!markingFormData.draft_contract_id
    const isBlockedByPublisher = markingFormData.publisher_flag

    const canChangeDates =
        !markingFormData.creative_flag && !markingFormData.final_contract_id

    const canAllowCheckbox =
        (!!deletedDate || duration === 'no_end_date') &&
        !!paymentMethod &&
        !!duration &&
        !!publishedAt

    const isDeletedDateDisabled = !canChangeDates || duration === 'no_end_date'

    const deletedDateTooltipMessage =
        duration === 'no_end_date'
            ? 'Выбрано "Без даты удаления"'
            : 'Если хотите отредактировать, то уберите галочку c "Токен получен" и удалите подписанный договор'

    const showError = (value: boolean) =>
        wasCheckboxClicked && !publishOptionFlag && !value

    const handleChangeCheckboxStatus = () => {
        if (!canAllowCheckbox) {
            setWasCheckboxClicked(true)
            return
        }

        if (isBlockedByContract || isBlockedByPublisher) {
            return
        }

        const newValue = !publishOptionFlag
        setValue('publishOptionFlag', newValue)
        updateMarkingFormData.mutate({
            publish_option_flag: newValue,
        })

        setPublishOptionError(false)
    }

    const handlePublishDateChange = (date: Date | undefined) => {
        if (!date) return

        const utcDate = toUtcDate(date)

        const autoDeletedDate = duration
            ? computeDeletedDate(duration, date)
            : undefined

        if (autoDeletedDate) {
            setValue('deletedDate', autoDeletedDate)
            updateMarkingFormData.mutate({
                publish_date: utcDate,
                deleted_date: toUtcDate(autoDeletedDate),
            })
        } else {
            updateMarkingFormData.mutate({
                publish_date: utcDate,
            })
        }
    }

    const handleSelectDuration = (
        option: SelectDropdownOptionType<
            string,
            string,
            MarkingPublishingDurationOptionType
        >
    ) => {
        setValue('duration', option.value)

        if (option.value === 'no_end_date') {
            setValue('deletedDate', undefined)
            updateMarkingFormData.mutate({
                duration: option.value,
                deleted_date: null,
            })
        } else if (option.value === 'no_duration') {
            updateMarkingFormData.mutate({
                duration: option.value,
            })
        } else {
            const autoDate = computeDeletedDate(option.value, publishedAt)
            if (autoDate) {
                setValue('deletedDate', autoDate)
                updateMarkingFormData.mutate({
                    duration: option.value,
                    deleted_date: toUtcDate(autoDate),
                })
            } else {
                console.log('f;jasdflk')
                updateMarkingFormData.mutate({
                    duration: option.value,
                })
            }
        }
    }

    const handleDeletedDateChange = (date: Date | undefined) => {
        if (!date) return

        updateMarkingFormData.mutate({
            deleted_date: toUtcDate(date),
        })
    }

    return {
        control,
        publishOptionFlag,
        duration,
        canChangeDates,
        canAllowCheckbox,
        isBlockedByContract,
        isBlockedByPublisher,
        isDeletedDateDisabled,
        deletedDateTooltipMessage,
        publishOptionError,
        showError,
        handlePublishDateChange,
        handleSelectDuration,
        handleDeletedDateChange,
        handleChangeCheckboxStatus,
    }
}
